import { type VirtualItem } from '@tanstack/react-virtual';
import * as React from 'react';

import { cn } from '../utils';

export interface DataGridCellProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Virtual column object containing position and size info */
  virtualColumn?: VirtualItem;
  /** Row index for calculating top position */
  rowIndex?: number;
  /** Height per row for calculating top position */
  rowHeight?: number;
  /** Absolute top position (used when rowIndex/rowHeight not provided) */
  top?: number;
  /** Absolute left position (used when virtualColumn not provided) */
  left?: number;
  /** Cell width (used when virtualColumn not provided) */
  width?: number;
  /** Cell height */
  height: number;
  /** Whether this cell is focused/highlighted */
  isFocused?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Offset to subtract from virtualColumn.start for proper positioning */
  leftOffset?: number;
}

/**
 * Individual virtualized cell for the main grid body.
 * Positioned absolutely within the grid body container.
 */
export const DataGridCell = React.forwardRef<HTMLDivElement, DataGridCellProps>(
  (
    {
      virtualColumn,
      rowIndex,
      rowHeight,
      top,
      left,
      width,
      height,
      isFocused = false,
      className,
      children,
      leftOffset = 0,
      ...props
    },
    ref,
  ) => {
    // Calculate position and size from virtualColumn/rowIndex or fallback to direct props
    const calculatedTop =
      top !== undefined
        ? top
        : rowIndex !== undefined && rowHeight !== undefined
          ? rowIndex * rowHeight
          : 0;

    const calculatedLeft = virtualColumn
      ? virtualColumn.start - leftOffset
      : (left ?? 0);
    const calculatedWidth = virtualColumn ? virtualColumn.size : (width ?? 0);

    return (
      <div
        role="table-cell"
        ref={ref}
        className={cn(
          'absolute flex items-center justify-center bg-white border-b border-grey-5',
          'transition-colors',
          isFocused && 'ring-2 ring-inset ring-primary',
          className,
        )}
        style={{
          top: calculatedTop,
          left: calculatedLeft,
          width: calculatedWidth,
          height,
        }}
        {...props}
      >
        {children}
      </div>
    );
  },
);

DataGridCell.displayName = 'DataGridCell';
